import { Cell, FormControl, Grid, HeadingSection } from 'bold-ui'
import {
  BairroSelectField,
  CepField,
  CheckboxField,
  FormRenderProps,
  LogradouroSelectField,
  MunicipioSelectField,
  SelectField,
  TextField,
  UfSelectField,
  usePecField,
} from 'components/form'
import { RecordStringSelectField } from 'components/form/core/RecordStringSelectField/RecordStringSelectField'
import { PaisSelectField } from 'components/form/field/select/PaisSelectField'

import {
  EnderecoResidenciaFormModel,
  metaNotificacaoAgravo,
  NotificacaoAgravoFormModel,
  zonaNotificacaoRecord,
} from '../model-notificacaoAgravo'

interface EnderecoResidenciaSectionProps {
  formProps: FormRenderProps<NotificacaoAgravoFormModel>
}

const meta = metaNotificacaoAgravo.dadosIndividuo.endereco

export function EnderecoResidenciaSection(props: Readonly<EnderecoResidenciaSectionProps>) {
  const { formProps } = props

  const {
    input: { value },
  } = usePecField<EnderecoResidenciaFormModel>({
    name: meta.absolutePath(),
    subscription: { value: true },
  })

  const semNumero = !!value?.semNumero

  const limparCampos = (...campos: Array<{ absolutePath(): string }>) =>
    campos.forEach((campo) => formProps.form.change(campo.absolutePath(), null))

  const handleBairroChange = () => {
    limparCampos(meta.logradouro, meta.numero, meta.complemento)
    formProps.form.change(meta.semNumero.absolutePath(), false)
  }

  const handleMunicipioChange = () => {
    limparCampos(meta.bairro)
    handleBairroChange()
  }

  const handleUfChange = () => {
    limparCampos(meta.municipio)
    handleMunicipioChange()
  }

  return (
    <HeadingSection level={3} title='Endereço de residência'>
      <Grid>
        <Cell size={6}>
          <PaisSelectField name={meta.pais} label='País de residência' required />
        </Cell>
        <Cell size={6}>
          <UfSelectField name={meta.uf} label='UF da residência' onChange={handleUfChange} required />
        </Cell>

        <Cell size={6}>
          <CepField name={meta.cep} label='CEP da residência' required />
        </Cell>

        <Cell size={12}>
          <LogradouroSelectField
            name={meta.logradouro}
            label='Logradouro'
            municipioId={value?.municipio?.id}
            bairroId={value?.bairro?.id}
            disabled={!value?.bairro}
            required
          />
        </Cell>

        <Cell size={3}>
          <TextField name={meta.numero} label='Nº' maxLength={10} disabled={semNumero} required={!semNumero} />
        </Cell>
        <Cell size={3} alignSelf='center'>
          <FormControl label={<span>&nbsp;</span>}>
            <CheckboxField name={meta.semNumero} label='Sem número' />
          </FormControl>
        </Cell>
        <Cell size={6}>
          <TextField name={meta.complemento} label='Complemento' maxLength={30} required />
        </Cell>

        <Cell size={6}>
          <BairroSelectField
            name={meta.bairro}
            label='Bairro'
            ufId={value?.uf?.id}
            municipioId={value?.municipio?.id}
            onChange={handleBairroChange}
            disabled={!value?.municipio}
            required
          />
        </Cell>
        <Cell size={6}>
          <MunicipioSelectField
            name={meta.municipio}
            label='Município'
            ufId={value?.uf?.id}
            onChange={handleMunicipioChange}
            disabled={!value?.uf}
            required
          />
        </Cell>

        <Cell size={6}>
          {/*
            TODO: não existe tabela de região administrativa no PEC — nenhuma entidade, enum ou
            query. O campo fica desabilitado até a carga chegar pelo back-end (issue #35076).
          */}
          <SelectField<string>
            name={meta.regiaoAdministrativa}
            label='Região administrativa'
            items={REGIOES_ADMINISTRATIVAS_PENDENTES}
            itemToString={(item) => item}
            disabled
          />
        </Cell>
        <Cell size={6}>
          <RecordStringSelectField name={meta.zona} record={zonaNotificacaoRecord} label='Zona' required />
        </Cell>
      </Grid>
    </HeadingSection>
  )
}

const REGIOES_ADMINISTRATIVAS_PENDENTES: string[] = []
